import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { GBP, ukDate } from "@/lib/format";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, Plus, Phone, Mail, AlertTriangle } from "lucide-react";

const empty = { first_name: "", last_name: "", email: "", mobile: "", dob: "", address: "", notes: "", is_regular: false };

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [detail, setDetail] = useState(null);
  const [patchDate, setPatchDate] = useState("");
  const logPatch = async () => {
    try { await api.post(`/clients/${detail.id}/patch-test`, { date: patchDate }); toast.success("Patch test logged"); setDetail((d) => ({ ...d, patch_test_date: patchDate })); setPatchDate(""); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const load = useCallback(async (s = "") => {
    const { data } = await api.get("/clients", { params: { search: s } });
    setClients(data);
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setTimeout(() => load(search), 300); return () => clearTimeout(t); }, [search, load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...empty, ...c }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) await api.put(`/clients/${editing.id}`, form);
      else await api.post("/clients", form);
      toast.success(editing ? "Client updated" : "Client added");
      setOpen(false); load(search);
      if (detail && editing) openDetail(editing.id);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const openDetail = async (id) => {
    const { data } = await api.get(`/clients/${id}`);
    setDetail(data);
  };

  return (
    <div className="max-w-6xl">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div><h1 className="font-extrabold text-3xl">Clients</h1><p className="text-stone-600 text-sm mt-1">{clients.length} in your database</p></div>
        <Button data-testid="add-client-button" onClick={openNew} className="rounded-full bg-stone-900 hover:bg-stone-800"><Plus className="mr-1.5 h-4 w-4" /> Add client</Button>
      </div>

      <div className="mt-5 relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
        <Input data-testid="client-search-input" placeholder="Search name, email or mobile…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-11 rounded-full" />
      </div>

      <div className="mt-5 bg-white rounded-2xl border border-stone-200 shadow-sm overflow-hidden">
        {clients.length === 0 ? (
          <p className="text-sm text-stone-500 p-6">No clients yet.</p>
        ) : (
          <div className="divide-y divide-stone-100">
            {clients.map((c) => (
              <button key={c.id} data-testid={`client-row-${c.id}`} onClick={() => openDetail(c.id)}
                className="w-full flex items-center gap-4 px-5 py-3.5 hover:bg-stone-50 text-left">
                <div className="h-10 w-10 rounded-full bg-[#F3EFEA] flex items-center justify-center font-semibold text-[#B07E4F]">
                  {(c.first_name?.[0] || "?").toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium">{c.first_name} {c.last_name}</div>
                  <div className="text-xs text-stone-500 flex items-center gap-3 mt-0.5">
                    {c.mobile && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{c.mobile}</span>}
                    {c.email && <span className="flex items-center gap-1 truncate"><Mail className="h-3 w-3" />{c.email}</span>}
                  </div>
                </div>
                {c.is_regular && <span data-testid={`client-regular-badge-${c.id}`} className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">Regular</span>}
                {c.no_show_count > 0 && <span className="flex items-center gap-1 text-xs text-purple-700 bg-purple-50 border border-purple-200 rounded-full px-2 py-0.5"><AlertTriangle className="h-3 w-3" />{c.no_show_count} no-show</span>}
                <div className="text-xs text-stone-400 hidden sm:block">Last: {c.last_appointment_at ? ukDate(c.last_appointment_at) : "—"}</div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? "Edit client" : "Add client"}</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>First name</Label><Input data-testid="client-first-name" value={form.first_name} onChange={set("first_name")} className="mt-1.5 h-10 rounded-lg" /></div>
            <div><Label>Last name</Label><Input data-testid="client-last-name" value={form.last_name} onChange={set("last_name")} className="mt-1.5 h-10 rounded-lg" /></div>
            <div><Label>Mobile</Label><Input data-testid="client-mobile" value={form.mobile} onChange={set("mobile")} className="mt-1.5 h-10 rounded-lg" /></div>
            <div><Label>Email</Label><Input data-testid="client-email" value={form.email} onChange={set("email")} className="mt-1.5 h-10 rounded-lg" /></div>
            <div><Label>Date of birth</Label><Input type="date" value={form.dob || ""} onChange={set("dob")} className="mt-1.5 h-10 rounded-lg" /></div>
            <div><Label>Address</Label><Input value={form.address} onChange={set("address")} className="mt-1.5 h-10 rounded-lg" /></div>
          </div>
          <div><Label>Notes</Label><Textarea data-testid="client-notes" value={form.notes} onChange={set("notes")} className="mt-1.5 rounded-lg" rows={3} /></div>
          <label className="flex items-start gap-2.5 text-sm cursor-pointer border border-stone-200 rounded-lg px-3 py-2.5">
            <input type="checkbox" data-testid="client-regular-checkbox" checked={!!form.is_regular} onChange={(e) => setForm((f) => ({ ...f, is_regular: e.target.checked }))} className="mt-0.5 h-5 w-5 accent-stone-900" />
            <span><b>Regular customer</b><span className="block text-xs text-stone-500">No deposit is asked for when they book online with this email.</span></span>
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
            <Button data-testid="save-client-button" onClick={save} disabled={!form.first_name} className="rounded-full bg-stone-900 hover:bg-stone-800">Save</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader><DialogTitle>{detail.first_name} {detail.last_name}</DialogTitle></DialogHeader>
              <div className="space-y-1 text-sm text-stone-600">
                {detail.mobile && <div className="flex items-center gap-2"><Phone className="h-4 w-4" />{detail.mobile}</div>}
                {detail.email && <div className="flex items-center gap-2"><Mail className="h-4 w-4" />{detail.email}</div>}
                {detail.dob && <div>DOB: {ukDate(detail.dob)}</div>}
                {detail.address && <div>{detail.address}</div>}
              </div>
              <div className="flex gap-4 text-sm">
                <span className="text-stone-500">Member since <b className="text-stone-800">{ukDate(detail.created_at)}</b></span>
                <span className="text-stone-500">No-shows <b className="text-stone-800">{detail.no_show_count}</b></span>
                {detail.is_regular && <span className="text-emerald-700 font-medium" data-testid="client-detail-regular">Regular · no deposit</span>}
              </div>
              <div className="flex flex-wrap items-center gap-2 text-sm border border-stone-200 rounded-lg px-3 py-2" data-testid="client-patch-test">
                <span className="text-stone-500">Patch test:</span>
                <b>{detail.patch_test_date ? ukDate(detail.patch_test_date) : "Not logged"}</b>
                <input type="date" value={patchDate} onChange={(e) => setPatchDate(e.target.value)} data-testid="patch-test-date" className="ml-auto border border-stone-300 rounded-lg px-2 py-1 text-sm" />
                <Button size="sm" className="rounded-full bg-stone-900" data-testid="patch-test-log" disabled={!patchDate} onClick={logPatch}>Log patch test</Button>
              </div>
              {detail.notes && <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900"><b>Notes:</b> {detail.notes}</div>}
              <div>
                <h3 className="font-semibold text-sm mb-2">Appointment history</h3>
                {detail.appointments.length === 0 ? <p className="text-sm text-stone-500">No appointments yet.</p> : (
                  <div className="space-y-2">
                    {detail.appointments.map((a) => (
                      <div key={a.id} className="flex items-center gap-3 text-sm border border-stone-100 rounded-lg px-3 py-2">
                        <span className="font-mono w-24 text-stone-600">{ukDate(a.date)}</span>
                        <div className="flex-1 min-w-0"><div className="truncate">{a.service_name}</div><div className="text-xs text-stone-500">{a.start} · {a.staff_name} · {GBP(a.price)}</div></div>
                        <StatusBadge status={a.status} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex justify-end"><Button variant="outline" className="rounded-full" onClick={() => openEdit(detail)}>Edit details</Button></div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
